import { useEffect, useRef } from "react";

const NOTHING: readonly string[] = [];

/**
 * The orrery's names, laid out once per view and then remembered.
 *
 * Seating forty names is the only work in this map a reader can feel,
 * and a plain `useMemo` throws it away the moment the camera moves: a
 * zoom changes the VIEW, not the atlas, and a reader who goes into a
 * ring nearly always comes back out.  Held one at a time, the sky was
 * laid out again on the way in and again on the way out — both times on
 * the click's own render, which is the pause that makes a tap feel
 * ignored.
 *
 * So every view is kept, and the whole cache is dropped the moment
 * anything underneath it moves.  Two things follow.  A revisit is a
 * lookup, so the camera starts gliding on the frame the tap lands.  And
 * because a revisit hands back the SAME answer, everything memoised
 * below it — forty planet nodes — can skip its own work too.
 *
 * The views NOT on screen are seated in the idle time after this one
 * paints, one per callback, so that work never competes with the glide
 * it exists to spare.  By the time a reader picks a ring, that ring is
 * usually already seated and its tap costs nothing either.
 *
 * `make` must be pure in its view and `deps`: this is a cache, and both
 * a repeated render and the warming pass are free to call it or not.
 */
export function useViewMemo<T>(
  key: string,
  make: (view: string) => T,
  deps: readonly unknown[],
  /** Every view worth having ready, in the order to prepare them.  Held
      by identity, so it must be memoised rather than built per render. */
  warm: readonly string[] = NOTHING,
): T {
  const cache = useRef<Map<string, T> | null>(null);
  const since = useRef<readonly unknown[]>(deps);
  const maker = useRef(make);
  maker.current = make;
  cache.current ??= new Map();

  if (deps.length !== since.current.length || deps.some((dep, i) => !Object.is(dep, since.current[i]))) {
    cache.current = new Map();
    since.current = deps;
  }
  if (!cache.current.has(key)) cache.current.set(key, make(key));

  // A new atlas empties the cache, so the warming pass is tied to the
  // same generation — `cache.current` is the identity that changes — and
  // a pass that finds itself working for the previous one stops.
  const generation = cache.current;
  useEffect(() => {
    const pending = warm.filter((view) => !generation.has(view));
    let idle: number | null = null;
    // Called as methods, not through a loose reference: a detached
    // `requestIdleCallback` is an illegal invocation in some browsers.
    // The timeout is the floor — a tab that never goes idle still warms.
    const schedule = (run: () => void) => {
      idle = window.requestIdleCallback
        ? window.requestIdleCallback(run, { timeout: 2000 })
        : window.setTimeout(run, 120);
    };
    const step = () => {
      idle = null;
      const next = pending.shift();
      if (next === undefined || generation !== cache.current) return;
      if (!generation.has(next)) generation.set(next, maker.current(next));
      schedule(step);
    };
    if (pending.length) schedule(step);
    return () => {
      if (idle === null) return;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
  }, [generation, warm]);

  return cache.current.get(key) as T;
}
